import { Link } from "react-router-dom";
import { useT } from "../i18n";
import { PLANS, formatPrice, href, CONTACTS, COMPANY } from "../content/shared";
import { PageHero } from "../components/kit";

const linkify = (text) =>
  String(text).split(/([\w.]+@chatplate\.com)/g).map((part, i) =>
    /@chatplate\.com$/.test(part) ? <a key={i} href={`mailto:${part}`} className="link">{part}</a> : part
  );

export function Legal({ doc }) {
  const { lang, t } = useT();
  const d = t.legal[doc];
  return (
    <>
      <PageHero eyebrow={d.eyebrow} lines={d.title} lead={d.intro} testid={`legal-${doc}`}>
        {d.updated && <p className="mt-6 text-sm text-muted-foreground">{d.updated}</p>}
      </PageHero>
      <section className="container-x grid gap-12 border-t border-border pb-24 pt-12 lg:grid-cols-[16rem_1fr]">
        <nav aria-label={t.common.onThisPage} className="hidden lg:sticky lg:top-28 lg:block lg:self-start">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t.common.onThisPage}</p>
          <ul className="mt-4 space-y-2 text-sm">{d.sections.map((s, i) => <li key={i}><a href={`#s-${i}`} className="text-muted-foreground hover:text-primary">{s.h}</a></li>)}</ul>
        </nav>
        <div className="max-w-3xl space-y-10">
          {d.sections.map((s, i) => (
            <section key={i} id={`s-${i}`} className="scroll-mt-28">
              <h2 className="text-xl md:text-2xl">{s.h}</h2>
              {s.p && <p className="mt-3 leading-relaxed text-muted-foreground">{linkify(s.p)}</p>}
              {s.plans && (
                <ul className="mt-4 grid gap-2 sm:grid-cols-3">
                  {PLANS.map((p) => <li key={p.id} className="rounded-xl border border-border bg-surface p-4"><span className="block text-sm text-muted-foreground">{t.pricing.names[p.id]}</span><span className="font-display text-xl font-semibold">{formatPrice(p.price, lang)}</span> <span className="text-sm text-muted-foreground">{t.pricing.per[p.id]}</span></li>)}
                </ul>
              )}
              {s.list && <ul className="mt-4 space-y-2">{s.list.map((x, j) => <li key={j} className="flex gap-3 text-muted-foreground"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{linkify(x)}</li>)}</ul>}
              {s.p2 && <p className="mt-3 leading-relaxed text-muted-foreground">{linkify(s.p2)}</p>}
              {s.company && (
                <address className="mt-4 not-italic text-muted-foreground">
                  <span className="font-medium text-foreground">{COMPANY.name}</span><br />{COMPANY.street}<br />{COMPANY.city}<br />{COMPANY.country}
                </address>
              )}
            </section>
          ))}
          <p className="border-t border-border pt-6 text-sm text-muted-foreground">
            {t.common.questions} <a className="link" href={`mailto:${CONTACTS.support}`}>{CONTACTS.support}</a> · <Link className="link" to={href(lang, "support")}>{t.nav.support}</Link>
          </p>
        </div>
      </section>
    </>
  );
}

export function Deletion() {
  const { lang, t } = useT();
  const d = t.deletion;
  return (
    <>
      <PageHero eyebrow={d.eyebrow} lines={d.h1} lead={d.lead} testid="deletion-hero" />
      <section className="container-x grid gap-12 border-t border-border pb-24 pt-12 lg:grid-cols-2">
        <div>
          <h2 className="text-2xl md:text-3xl">{d.stepsH}</h2>
          <ol className="mt-8 space-y-5">
            {d.steps.map((s, i) => <li key={i} className="grid grid-cols-[2.5rem_1fr] gap-4 border-b border-border pb-5"><span className="font-mono text-sm text-primary">0{i + 1}</span><span>{s}</span></li>)}
          </ol>
          <a href={`mailto:${CONTACTS.support}?subject=${encodeURIComponent(d.subject)}`} className="btn-primary mt-8" data-testid="deletion-email-btn">{d.cta}</a>
        </div>
        <div className="rounded-2xl border border-border bg-surface p-7 md:p-9">
          <h2 className="text-xl md:text-2xl">{d.notesH}</h2>
          <ul className="mt-5 space-y-3">{d.notes.map((n, i) => <li key={i} className="flex gap-3 text-muted-foreground"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{n}</li>)}</ul>
          <Link to={href(lang, "privacy")} className="link mt-6 inline-block text-sm">{t.footer.privacy}</Link>
        </div>
      </section>
    </>
  );
}
